import * as React from 'react';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { List } from '@fluentui/react/lib/List';
import { Image, ImageFit } from '@fluentui/react/lib/Image';
import { ReportItem, ReportStatus } from '../../types'; // Ensure the path is correct
import { useDrop, useDrag, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Modal } from '@fluentui/react/lib/Modal';
import DocumentViewer from '../DocumentViewer';
import { getStatusColor } from '../../utilities/statusColors';
import styles from './ReportList.module.scss';
import { IRectangle } from '@fluentui/react/lib/Utilities';

const ReportItemContent = <T extends ReportItem>({
  item,
  index,
  moveItem,
  classNames,
  onCheckboxChange,
  onItemClick,
  renderContent = (item: T) => null,
  renderTitle = (item: T) => null,
  renderDescription = (item: T) => null,
  showPlaceholderIcon = true,
}: {
  item: T;
  index: number;
  moveItem: any;
  classNames: any;
  onCheckboxChange: (checked: boolean, index: number) => void;
  onItemClick?: (item: T) => void;
  renderContent?: (item: any) => React.ReactNode;
  renderTitle?: (item: T) => React.ReactNode;
  renderDescription?: (item: T) => React.ReactNode;
  showPlaceholderIcon?: boolean;
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const [showModalType, setShowModalType] = React.useState<'view' | 'repair'>('view');

  const [, drag] = useDrag({
    type: 'report',
    item: { index },
  });

  const [, drop] = useDrop({
    accept: 'report',
    hover(item: { index: number }) {
      if (item.index !== index) {
        moveItem(item.index, index);
        item.index = index;
      }
    },
  });

  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger click when clicking checkbox
    if ((e.target as HTMLElement).closest('.ms-Checkbox')) {
      return;
    }
    onItemClick?.(item);
  };

  return (
    <div
      ref={node => drag(drop(node))}
      className={`${styles.itemCell} ${item.status === ReportStatus.Processing ? styles.processingItem : ''} ${styles.clickable}`}
      data-is-focusable={true}
      onClick={handleClick}
    >
      <Checkbox
        checked={item.checked}
        onChange={(_, checked) => onCheckboxChange(!!checked, index)}
        className={styles.checkbox}
        disabled={item.status === ReportStatus.Processing}
      />
      {showPlaceholderIcon && (
        <Image
          className="itemImage"
          src="https://res.cdn.office.net/files/fabric-cdn-prod_20230815.002/office-ui-fabric-react-assets/fluent-placeholder.svg"
          width={50}
          height={50}
          imageFit={ImageFit.cover}
          styles={{ root: { marginRight: '1rem' } }}
        />
      )}
      {renderContent(item) || (
        <div className={styles.itemContent}>
          <div className={styles.itemName}>{renderTitle(item) || item.filename}</div>
          <div className={styles.itemIndex} style={{ color: getStatusColor(item.status) }}>
            {item.status}
          </div>
          {renderDescription && <div className={styles.itemIndex}>{renderDescription(item)}</div>}
        </div>
      )}
      <Modal isOpen={showModal} onDismiss={() => setShowModal(false)} isBlocking={true}>
        {showModalType === 'view' && <DocumentViewer />}
        {showModalType === 'repair' && <div>Repair</div>}
      </Modal>
    </div>
  );
};

// Define the imperative handle type
export interface ReportListHandle {
  clearCheckedItems: () => void;
}

interface ReportListProps<T extends ReportItem> {
  items: T[];
  onChecked?: (checkedItems: T[]) => void;
  onItemClick?: (item: T) => void;
  orderBy?: { property: keyof T; desc?: boolean };
  renderContent?: (item: T) => React.ReactNode;
  renderTitle?: (item: T) => React.ReactNode;
  renderDescription?: (item: T) => React.ReactNode;
  showPlaceholderIcon?: boolean;
}

const ReportList = React.forwardRef(<T extends ReportItem>(
  props: ReportListProps<T>,
  ref: React.ForwardedRef<ReportListHandle>
) => {
  const {
    items,
    onChecked,
    onItemClick,
    orderBy,
    renderContent,
    renderTitle,
    renderDescription,
    showPlaceholderIcon = true,
  } = props;

  // Sort items based on orderBy prop
  const sortedItems = React.useMemo(() => {
    if (orderBy) {
      const { property, desc } = orderBy;
      const sorted = [...items].sort((a, b) => {
        // Handle null or undefined values for comparison
        const valueA = a[property] ?? '';
        const valueB = b[property] ?? '';
        
        // For date fields specifically, convert to Date objects for proper comparison
        // Check for known date property names first
        if (
          property === 'created date' || 
          property === 'created' || 
          property.toString().toLowerCase().includes('date')
        ) {
          // Handle empty values
          if (!valueA) return desc ? -1 : 1;
          if (!valueB) return desc ? 1 : -1;
          
          // Convert string dates to Date objects for proper comparison
          const dateA = new Date(valueA.toString());
          const dateB = new Date(valueB.toString());
          
          // Check if valid dates (Invalid dates will return NaN for getTime())
          if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
            // Fall back to string comparison if invalid dates
            const strA = String(valueA);
            const strB = String(valueB);
            if (strA < strB) return desc ? 1 : -1;
            if (strA > strB) return desc ? -1 : 1;
            return 0;
          }
          
          // Compare dates using getTime() for accurate timestamp comparison
          return desc ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime();
        }
        
        // Default string comparison for non-date fields
        const strA = String(valueA);
        const strB = String(valueB);
        if (strA < strB) return desc ? 1 : -1;
        if (strA > strB) return desc ? -1 : 1;
        return 0;
      });
      return sorted;
    }
    return items;
  }, [items, orderBy]);

  // Ensure processing items are always at the top
  const sortedAndFilteredItems = React.useMemo(() => {
    const processingItems = sortedItems.filter(item => item.status === ReportStatus.Processing);
    const enqueuedItems = sortedItems.filter(item => item.status === ReportStatus.Enqueued);
    const nonProcessingItems = sortedItems.filter(
      item => item.status !== ReportStatus.Processing && item.status !== ReportStatus.Enqueued,
    );
    
    const result = [...processingItems, ...enqueuedItems, ...nonProcessingItems];
    return result;
  }, [sortedItems]);

  const [reportItems, setReportItems] = React.useState<(T & { checked?: boolean })[]>([]);
  const listRef = React.useRef<List<T & { checked?: boolean }>>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Track the scrolling to ensure all items load
  const [renderedWindowsAhead, setRenderedWindowsAhead] = React.useState(2);
  const [renderedWindowsBehind, setRenderedWindowsBehind] = React.useState(2);

  // Update reportItems when sortedAndFilteredItems change
  React.useEffect(() => {
    setReportItems(prevItems => {
      // Create a map of previous checked states
      const checkedMap = new Map(prevItems.map(item => [item.filename, item.checked]));

      // Preserve checked state for existing items
      const newItems = sortedAndFilteredItems.map(item => ({
        ...item,
        checked: checkedMap.get(item.filename) || false,
      }));
      
      return newItems;
    });
  }, [sortedAndFilteredItems]);

  React.useEffect(() => {
    // When items change, ensure the list is re-measured
    if (listRef.current) {
      listRef.current.forceUpdate();
    }
  }, [reportItems]);

  const moveItem = React.useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragItem = reportItems[dragIndex];
      if (!dragItem) {
        console.warn(`moveItem: dragItem at index ${dragIndex} is undefined`);
        return;
      }
      
      const updatedItems = [...reportItems];
      updatedItems.splice(dragIndex, 1);
      updatedItems.splice(hoverIndex, 0, dragItem);
      setReportItems(updatedItems); // Update the state with the new array
    },
    [reportItems],
  );

  const handleCheckboxChange = React.useCallback(
    (checked: boolean, index: number) => {
      const updatedItems = [...reportItems];
      if (index >= 0 && index < updatedItems.length) {
        updatedItems[index].checked = checked;
        setReportItems(updatedItems);
        if (onChecked) {
          onChecked(updatedItems.filter(item => item.checked) as T[]); // Notify parent component if onChecked is provided
        }
      } else {
        console.warn(`handleCheckboxChange: Index ${index} out of bounds (length: ${updatedItems.length})`);
      }
    },
    [reportItems, onChecked],
  );

  // Define a method to clear all checked items
  const clearCheckedItems = React.useCallback(() => {
    setReportItems(prevItems => {
      const newItems = prevItems.map(item => ({
        ...item,
        checked: false,
      }));
      
      // Notify parent that all items are unchecked
      if (onChecked) {
        onChecked([]);
      }
      
      return newItems;
    });
  }, [onChecked]);

  // Expose the clearCheckedItems method via ref
  React.useImperativeHandle(ref, () => ({
    clearCheckedItems,
  }), [clearCheckedItems]);

  // If there are no items, return empty div to prevent errors
  if (reportItems.length === 0 && items.length === 0) {
    return <div className={styles.emptyList}></div>;
  }

  // Calculate an appropriate page height based on the known height of items
  const getItemCountForPage = (itemIndex?: number, surfaceRect?: IRectangle) => {
    if (!itemIndex || !surfaceRect) {
      return Math.min(20, reportItems.length);
    }
    
    if (itemIndex === 0) {
      // First render - use a small batch to render quickly
      return Math.min(20, reportItems.length);
    }
    // For subsequent renders, load more items to ensure smooth scrolling
    // Calculate approximately how many items can fit on screen + buffer
    const itemHeight = 54; // Minimum height of an item in pixels
    const visibleCount = Math.ceil(surfaceRect.height / itemHeight) * 3; // Triple the visible count for buffer
    return Math.min(visibleCount, reportItems.length - itemIndex);
  };

  // Ensure all items can be seen when scrolling
  const onShouldVirtualize = () => {
    // Disable virtualization for smaller lists, enable for larger ones
    return reportItems.length > 100;
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <FocusZone direction={FocusZoneDirection.vertical}>
        <div ref={containerRef} className={styles.container} data-is-scrollable>
          <List
            ref={listRef}
            items={reportItems}
            onShouldVirtualize={onShouldVirtualize}
            getItemCountForPage={getItemCountForPage}
            renderedWindowsAhead={renderedWindowsAhead}
            renderedWindowsBehind={renderedWindowsBehind}
            onRenderCell={(item, index) => {
              if (!item || index === undefined) {
                console.warn('ReportList: Attempted to render undefined item or index');
                return null;
              }
              return (
                <ReportItemContent
                  item={item as T}
                  index={index}
                  moveItem={moveItem}
                  classNames=""
                  onCheckboxChange={handleCheckboxChange}
                  onItemClick={onItemClick}
                  renderContent={renderContent}
                  renderTitle={renderTitle}
                  renderDescription={renderDescription}
                  showPlaceholderIcon={showPlaceholderIcon}
                />
              );
            }}
          />
        </div>
      </FocusZone>
    </DndProvider>
  );
}) as <T extends ReportItem>(props: ReportListProps<T> & { ref?: React.ForwardedRef<ReportListHandle> }) => JSX.Element;

export default ReportList;
